import { Stat, Stats, Empty } from '../office';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@verlo/shared/components/ui/card';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Label } from '@verlo/shared/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@verlo/shared/components/ui/dialog';
import { Plus, Beaker, Scale, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { subRecipesApi } from '@verlo/shared/services/api';

const formatCurrency = (value) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
const emptyForm = { name: '', yield_qty: '', yield_unit: '', batch_cost: '' };

// The sub-recipes panel (menu consolidation, wave 2): rendered inside Recipes under its own
// tab, so sauces, bases and preps live next to the dishes that use them.
const SubRecipesPanel = () => {
  const [subRecipes, setSubRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await subRecipesApi.getAll();
      setSubRecipes(res.data || []);
    } catch {
      toast.error('Could not load sub-recipes');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({ name: r.name, yield_qty: String(r.yield_qty ?? ''), yield_unit: r.yield_unit || '', batch_cost: String(r.batch_cost ?? '') });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      yield_qty: parseFloat(form.yield_qty) || 0,
      yield_unit: form.yield_unit.trim(),
      batch_cost: parseFloat(form.batch_cost) || 0,
    };
    try {
      if (editingId) await subRecipesApi.update(editingId, payload);
      else await subRecipesApi.create(payload);
      toast.success(editingId ? 'Sub-recipe updated' : 'Sub-recipe added');
      setDialogOpen(false);
      await load();
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    try {
      await subRecipesApi.delete(r.id);
      toast.success('Sub-recipe deleted');
      await load();
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const totalCost = subRecipes.reduce((a, r) => a + (r.batch_cost || 0), 0);
  const avgCost = subRecipes.length ? totalCost / subRecipes.length : 0;

  return (
    <div className="space-y-6" data-testid="sub-recipes-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-[13.5px] text-ink-soft">Sauces, bases and preps that recipes build on: what a batch makes, and what it costs.</p>
        <Button className="bg-verlo hover:bg-verlo-hover" onClick={openAdd} data-testid="subrecipe-add">
          <Plus size={16} className="mr-2" /> New sub-recipe
        </Button>
      </div>

      <Stats>
        <Stat label="Sub-recipes" value={subRecipes.length} testId="subrecipe-count" />
        <Stat label="Average batch cost" value={formatCurrency(avgCost)} />
        <Stat label="All batches" value={formatCurrency(totalCost)} />
      </Stats>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />)}
        </div>
      ) : subRecipes.length === 0 ? (
        <Empty word="Sub-recipes" title="No sub-recipes yet" testId="subrecipe-empty"
          action={<Button variant="outline" onClick={openAdd}>Add the first</Button>}>
          A sauce, a brine, a dough: anything made in a batch and used by more than one dish.
        </Empty>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subRecipes.map((recipe) => (
            <Card key={recipe.id} data-testid="subrecipe-row" data-id={recipe.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-signal-info-tint flex items-center justify-center">
                      <Beaker className="text-signal-info" size={20} />
                    </div>
                    <CardTitle className="text-base">{recipe.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(recipe)} data-testid={`subrecipe-edit-${recipe.id}`}>
                      <Pencil size={13} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-signal-stop hover:text-signal-stop" onClick={() => remove(recipe)} data-testid={`subrecipe-delete-${recipe.id}`}>
                      <Trash2 size={13} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-ink-soft">
                    <Scale size={14} />
                    <span>Yield: {recipe.yield_qty || 0}{recipe.yield_unit ? ` ${recipe.yield_unit}` : ''}</span>
                  </div>
                  <div className="font-semibold text-ink font-mono">{formatCurrency(recipe.batch_cost)}/batch</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="subrecipe-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit sub-recipe' : 'New sub-recipe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Hot Honey Glaze" data-testid="subrecipe-name-input" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Yield</Label>
                <Input type="number" step="0.1" value={form.yield_qty} onChange={(e) => setForm({ ...form, yield_qty: e.target.value })} data-testid="subrecipe-yield-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Unit</Label>
                <Input value={form.yield_unit} onChange={(e) => setForm({ ...form, yield_unit: e.target.value })} placeholder="e.g. L or kg" data-testid="subrecipe-unit-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Batch cost £</Label>
                <Input type="number" step="0.01" value={form.batch_cost} onChange={(e) => setForm({ ...form, batch_cost: e.target.value })} data-testid="subrecipe-cost-input" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-verlo hover:bg-verlo-hover gap-1.5" onClick={save} disabled={saving} data-testid="subrecipe-save">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editingId ? 'Save changes' : 'Add sub-recipe'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubRecipesPanel;
